import {
  CANVAS_ELEMENT_TYPES,
  createCanvasElementId,
  moveCanvasElement,
} from "../nativeCanvasElements";
import { getSelectionBounds } from "../geometry/elementBounds";

export const CANVAS_CLIPBOARD_KIND = "netpad.canvas/clipboard";
const CANVAS_CLIPBOARD_VERSION = 1;

const isClipboardElement = (candidate) =>
  Boolean(candidate) &&
  typeof candidate === "object" &&
  Boolean(candidate.id) &&
  Boolean(candidate.type);

/**
 * @param {Array<object>} elements
 * @returns {string} JSON payload written to the system clipboard on copy
 */
export const serializeElementsForClipboard = (elements) =>
  JSON.stringify({
    elements,
    kind: CANVAS_CLIPBOARD_KIND,
    version: CANVAS_CLIPBOARD_VERSION,
  });

/**
 * @param {string} text - clipboard text, possibly from another app
 * @returns {Array<object> | null} canvas elements, or null when the text is
 * not a Netpad canvas clipboard payload
 */
export const parseClipboardElements = (text) => {
  if (typeof text !== "string" || !text.includes(CANVAS_CLIPBOARD_KIND)) {
    return null;
  }
  let parsedPayload;
  try {
    parsedPayload = JSON.parse(text);
  } catch {
    return null;
  }
  if (parsedPayload?.kind !== CANVAS_CLIPBOARD_KIND) return null;
  if (!Array.isArray(parsedPayload.elements)) return null;

  const elements = parsedPayload.elements.filter(isClipboardElement);
  return elements.length ? elements : null;
};

const remapArrowBindings = ({ bindings, idMap }) => {
  const remapBinding = (binding) =>
    binding && idMap.has(binding.elementId)
      ? { ...binding, elementId: idMap.get(binding.elementId) }
      : undefined;
  const start = remapBinding(bindings?.start);
  const end = remapBinding(bindings?.end);

  return start || end ? { end, start } : undefined;
};

/**
 * Prepares copied elements for insertion: fresh ids, the whole group centered
 * on `targetPoint`, and arrow bindings remapped to the new ids — bindings to
 * elements outside the copied set are dropped.
 * @param {{ elements: Array<object>, targetPoint: { x: number, y: number } }} params
 * @returns {Array<object>}
 */
export const prepareElementsForPaste = ({ elements, targetPoint }) => {
  const bounds = getSelectionBounds(elements);
  if (!bounds) return [];

  const delta = {
    x: targetPoint.x - (bounds.x + bounds.width / 2),
    y: targetPoint.y - (bounds.y + bounds.height / 2),
  };
  const idMap = new Map(
    elements.map((element) => [element.id, createCanvasElementId()])
  );

  return elements.map((element) => {
    const movedElement = {
      ...moveCanvasElement({ delta, element }),
      id: idMap.get(element.id),
    };
    if (element.type !== CANVAS_ELEMENT_TYPES.ARROW) return movedElement;

    return {
      ...movedElement,
      bindings: remapArrowBindings({ bindings: element.bindings, idMap }),
    };
  });
};
